import { CircularProgress, Divider, Grid, Rating, Skeleton } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PageLayout from "pages/layout/page-layout";
import SoftProgress from "components/SoftProgress";
import StarIcon from "@mui/icons-material/Star";
import { get, SERVER_URL_PREFIX } from "api";
import { useEffect, useState } from "react";
import SoftPagination from "components/SoftPagination";
import PropTypes from "prop-types";
import ReviewCard from "pages/user/review-card";
import Pagination from "components/Pagination";
import { circularProgressClasses } from "@mui/material/CircularProgress";
import { useDocumentTitle } from "hooks/use-document-title";
import { useMetaTag } from "hooks/use-document-title";

const Reviews = ({ productIds = [], sx }) => {
  useDocumentTitle("Recenzije | Mama za pet kozmetički proizvodi");
  useMetaTag(
    "description",
    "Pročitajte recenzije Mama za pet proizvoda. Pogledajte šta naše kupce najviše oduševljava."
  );
  useMetaTag(
    "keywords",
    "mama za pet recenzije, mama za pet iskustva, mama za pet utisci, mama za pet kupci, mama za pet proizvodi recenzije"
  );

  const [page, setPage] = useState(0);
  const [overallRating, setOverallRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  const [reviewsResponse, setreviewsResponse] = useState(null);
  const [reviewsResponseLoading, setReviewsResponseLoading] = useState(true);
  const [reviewsResponseError, setReviewsResponseError] = useState(null);

  const [overallStatistics, setOverallStatistics] = useState([]);
  const [overallStatisticsLoading, setOverallStatisticsLoading] = useState(true);
  const [overallStatisticsError, setOverallStatisticsError] = useState(null);

  const [selectedImage, setSelectedImage] = useState(null);
  const handleImageClick = (imageUrl) => setSelectedImage(imageUrl);

  const renderImagePreview = () =>
    selectedImage ? (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: "9999",
        }}
        onClick={() => setSelectedImage(null)}
      >
        <img src={selectedImage} alt="Preview" style={{ maxWidth: "80%", maxHeight: "80%" }} />
      </div>
    ) : null;

  useEffect(() => {
    const fetchReviews = async () => {
      setReviewsResponseLoading(true);
      setReviewsResponseError(null);
      try {
        const response =
          productIds.length > 0
            ? await get(
                `${SERVER_URL_PREFIX}/api/web/review?productIds=${productIds.join(
                  ","
                )}&size=4&page=${page}`
              )
            : await get(`${SERVER_URL_PREFIX}/api/web/review/approved?size=8&page=${page}`);
        const data = await response.json();
        setreviewsResponse(data);
      } catch (error) {
        setReviewsResponseError(error);
      } finally {
        setReviewsResponseLoading(false);
      }
    };
    fetchReviews();
  }, [page]); // productIds intentionally omitted (keeps your current behavior)

  useEffect(() => {
    const fetchOverallStatistics = async () => {
      setOverallStatisticsLoading(true);
      setOverallStatisticsError(null);
      try {
        const response =
          productIds?.length > 0
            ? await get(
                `${SERVER_URL_PREFIX}/api/web/review/statistics/products?productIds=${productIds.join(
                  ","
                )}`
              )
            : await get(`${SERVER_URL_PREFIX}/api/web/review/statistics/overall`);
        const data = await response.json();
        setOverallStatistics(data);
      } catch (error) {
        setOverallStatisticsError(error);
      } finally {
        setOverallStatisticsLoading(false);
      }
    };
    fetchOverallStatistics();
  }, []);

  const calculateOverallAverageRating = (data) => {
    const { ratingCounts } = data;
    let totalRatings = 0;
    let totalVotes = 0;

    Object.keys(ratingCounts).forEach((rating) => {
      const count = ratingCounts[rating];
      totalRatings += rating * count;
      totalVotes += count;
    });

    return {
      averageRating: totalVotes > 0 ? totalRatings / totalVotes : 0,
      totalReviews: totalVotes,
    };
  };

  useEffect(() => {
    if (overallStatistics?.ratingCounts) {
      const ratingData = calculateOverallAverageRating(overallStatistics);
      setOverallRating(ratingData.averageRating.toFixed(1));
      setTotalReviews(ratingData.totalReviews);
    }
  }, [overallStatistics]);

  const generateProgress = (label, value, color) => (
    <Grid container alignItems="center">
      <Grid item xs={3} md={2} sx={{ padding: "0 !important" }}>
        <SoftTypography variant="caption">{label}</SoftTypography>
      </Grid>
      <Grid item xs={8} md={7} sx={{ padding: "0 !important", marginTop: "6px" }}>
        <SoftProgress value={Number(value)} color={color} sx={{ width: "100%" }} />
      </Grid>
      <Grid
        item
        xs={1}
        sx={{ padding: "0 !important", display: "flex", justifyContent: "flex-end" }}
      >
        <SoftTypography variant="caption">{value}%</SoftTypography>
      </Grid>
    </Grid>
  );

  const scrollToElement = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const calculateRatingByCount = (ratingIndex) => {
    if (totalReviews === 0) return 0;
    return Math.round((overallStatistics.ratingCounts[ratingIndex] / totalReviews) * 100);
  };

  const renderPagination = () => (
    <Pagination
      totalPages={reviewsResponse.totalPages}
      currentPage={page}
      onPageChange={(newPage) => {
        scrollToElement("review-heading-container");
        setPage(newPage);
      }}
    />
  );

  if (reviewsResponseLoading && overallStatisticsLoading) {
    return (
      <PageLayout
        sx={{
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          ...sx,
        }}
      >
        <Divider sx={{ width: "100%" }} />
        <SoftBox display="flex" justifyContent="center" id="review-heading-container">
          <SoftTypography variant="h3">Recenzije</SoftTypography>
        </SoftBox>
        <Divider sx={{ width: "100%", marginBottom: "40px" }} />
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          gap="3rem"
        >
          <SoftTypography variant="h5">Recenzije se učitavaju...</SoftTypography>
          <CircularProgress
            variant="indeterminate"
            disableShrink
            sx={{
              color: "#7eddd3",
              animationDuration: "550ms",
              [`& .${circularProgressClasses.circle}`]: { strokeLinecap: "round" },
            }}
            size={40}
            thickness={4}
          />
        </SoftBox>
      </PageLayout>
    );
  }

  const ReviewSkeleton = () => (
    <SoftBox mx="1rem">
      <Skeleton variant="text" width={140} height={18} sx={{ mb: 1 }} /> {/* name/date */}
      <Skeleton variant="rectangular" height={72} sx={{ borderRadius: "8px", mb: 1 }} />{" "}
      <Skeleton variant="text" width={100} height={16} /> {/* product line or tag */}
      <Divider sx={{ width: "100%", mt: 2 }} />
    </SoftBox>
  );

  return (
    <PageLayout
      sx={{ overflow: "hidden", display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <Divider sx={{ width: "100%" }} />
      <SoftBox display="flex" justifyContent="center" id="review-heading-container">
        <SoftTypography variant="h3">Recenzije</SoftTypography>
      </SoftBox>
      <Divider sx={{ width: "100%", marginBottom: "40px" }} />

      {/* SUMMARY BLOCK */}
      <SoftBox display="flex" justifyContent="center" width="100%">
        <Grid container maxWidth="1000px" width="100%">
          <Grid item xs={12} md={6} display="flex" flexDirection="column" justifyContent="center">
            <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight={162}>
              {overallStatisticsLoading ? (
                <SoftBox display="flex" flexDirection="column" alignItems="center" gap="10px">
                  <Skeleton variant="text" width={60} height={48} />
                  <Skeleton variant="text" width={120} height={24} />
                  <Skeleton variant="text" width={160} height={16} />
                </SoftBox>
              ) : (
                <SoftBox display="flex" flexDirection="column" alignItems="center" gap="10px">
                  <SoftTypography variant="h2">{overallRating}</SoftTypography>
                  <Rating
                    name="text-feedback"
                    value={Number(overallRating)}
                    readOnly
                    precision={0.1}
                    emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                  />
                  <SoftTypography variant="caption">
                    Na osnovu{" "}
                    <SoftTypography variant="caption" sx={{ fontWeight: "bold" }}>
                      {totalReviews}
                    </SoftTypography>{" "}
                    recenzija
                  </SoftTypography>
                </SoftBox>
              )}
            </SoftBox>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            flexDirection="column"
            alignItems="center"
            mt={{ xs: "3rem", md: "0" }}
            mx={{ xs: "1rem", md: "0" }}
          >
            {overallStatisticsLoading ? (
              <>
                <Skeleton
                  variant="rectangular"
                  height={16}
                  width="100%"
                  sx={{ mb: 1, maxWidth: 440 }}
                />
                <Skeleton
                  variant="rectangular"
                  height={16}
                  width="100%"
                  sx={{ mb: 1, maxWidth: 440 }}
                />
                <Skeleton
                  variant="rectangular"
                  height={16}
                  width="100%"
                  sx={{ mb: 1, maxWidth: 440 }}
                />
                <Skeleton
                  variant="rectangular"
                  height={16}
                  width="100%"
                  sx={{ mb: 1, maxWidth: 440 }}
                />
                <Skeleton variant="rectangular" height={16} width="100%" sx={{ maxWidth: 440 }} />
              </>
            ) : (
              <>
                {generateProgress("Odlično", calculateRatingByCount(5), "#56a545")}
                {generateProgress("Vrlo dobro", calculateRatingByCount(4), "#a9d616")}
                {generateProgress("Dobro", calculateRatingByCount(3), "#f4e502")}
                {generateProgress("Loše", calculateRatingByCount(2), "#f0a400")}
                {generateProgress("Veoma loše", calculateRatingByCount(1), "#e53700")}
              </>
            )}
          </Grid>
        </Grid>
      </SoftBox>

      <Divider sx={{ width: "100%", marginBottom: "40px", marginTop: "40px" }} />

      {/* REVIEWS LIST */}
      <SoftBox
        display="flex"
        justifyContent="center"
        flexDirection="column"
        maxWidth="900px"
        width="100%"
        gap="30px"
      >
        {reviewsResponseLoading ? (
          // show skeletons while reviews are loading (even if summary is ready)
          <>
            <ReviewSkeleton />
            <ReviewSkeleton />
            <ReviewSkeleton />
            <ReviewSkeleton />
          </>
        ) : reviewsResponse && reviewsResponse.content && reviewsResponse.content.length > 0 ? (
          reviewsResponse.content.map((review) => (
            <SoftBox key={review.customerName + review.product.id + review.createdAt} mx="1rem">
              <ReviewCard
                name={review.customerName}
                rating={Number(review.rating)}
                text={review.review}
                date={review.createdAt}
                images={review.images}
                product={review.product}
                onImageClick={handleImageClick}
              />
              <Divider sx={{ width: "100%" }} />
            </SoftBox>
          ))
        ) : reviewsResponseError || overallStatisticsError ? (
          <SoftTypography variant="body2" color="error" sx={{ textAlign: "center" }}>
            Greška prilikom učitavanja recenzija
          </SoftTypography>
        ) : (
          <SoftTypography variant="body2" sx={{ textAlign: "center" }}>
            Nema recenzija za prikaz
          </SoftTypography>
        )}

        {reviewsResponse &&
          reviewsResponse.content &&
          reviewsResponse.content.length > 0 &&
          !reviewsResponseLoading && (
            <SoftPagination mr="1rem">{renderPagination()}</SoftPagination>
          )}
      </SoftBox>

      {selectedImage && renderImagePreview()}
    </PageLayout>
  );
};

Reviews.propTypes = {
  productIds: PropTypes.array,
  sx: PropTypes.object,
};

export default Reviews;
